import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProjects } from "../lib/ProjectContext";
import { useCurrentAppUser } from "../lib/useCurrentAppUser";
import { api } from "../lib/apiClient";
import type { DashboardData } from "@shared/types";

/**
 * Deliberately reuses the same /api/projects/:id/dashboard call
 * DashboardPage already makes rather than a new "notifications" endpoint —
 * needsAttention (documents awaiting owner approval, OR an already-approved
 * document's agent-drafted tables still sitting in review_status=
 * 'pending_review' — see DashboardNeedsAttentionItem's `reason`) and
 * openEscalations (expert questions waiting on a reply) are the only real,
 * already-tracked signals in this app that resemble a notification feed. No
 * fabricated data, no new backend surface.
 */
export function NotificationsBell() {
  const { selectedProjectId } = useProjects();
  const { role } = useCurrentAppUser();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedProjectId) {
      setData(null);
      return;
    }
    let cancelled = false;
    api
      .get<DashboardData>(`/api/projects/${selectedProjectId}/dashboard`)
      .then((result) => !cancelled && setData(result))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Only owners act on a pending document, so only they get counted/shown
  // for it — matches DashboardPage's "Review" button, which is owner-only too.
  const needsAttention = role === "owner" ? (data?.needsAttention ?? []) : [];
  const openEscalations = data?.stats.openEscalations ?? 0;
  const count = needsAttention.length + openEscalations;

  function go(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <div className="topbar-icon-wrap" ref={wrapRef}>
      <button
        type="button"
        className="topbar-icon-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Notifications"
        title="Notifications"
      >
        <svg width="17" height="17" viewBox="0 0 16 16" fill="none">
          <path d="M4 6.5a4 4 0 0 1 8 0c0 3 1 4 1 4H3s1-1 1-4Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
          <path d="M6.5 12.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
        {count > 0 && <span className="topbar-icon-badge">{count > 9 ? "9+" : count}</span>}
      </button>
      {open && (
        <div className="topbar-dropdown" role="menu" aria-label="Notifications">
          <div className="topbar-dropdown-title">Notifications</div>
          {count === 0 ? (
            <div className="topbar-dropdown-empty">You're all caught up.</div>
          ) : (
            <>
              {openEscalations > 0 && (
                <button type="button" role="menuitem" className="topbar-dropdown-item" onClick={() => go("/escalations")}>
                  <span className="topbar-dropdown-item-title">
                    {openEscalations} expert question{openEscalations === 1 ? "" : "s"} waiting on a reply
                  </span>
                </button>
              )}
              {needsAttention.slice(0, 5).map((item) => (
                <button
                  key={`${item.documentId}-${item.reason}`}
                  type="button"
                  role="menuitem"
                  className="topbar-dropdown-item"
                  onClick={() => go("/sources")}
                >
                  <span className="topbar-dropdown-item-title">{item.title}</span>
                  <span className="topbar-dropdown-item-sub">
                    {item.reason === "table_review"
                      ? `${item.pendingTableCount} table${item.pendingTableCount === 1 ? "" : "s"} need${item.pendingTableCount === 1 ? "s" : ""} review`
                      : `${item.sourceType.replace(/_/g, " ")} · awaiting approval`}
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
